import React from 'react';
import moment from 'moment';
import { InfoIcon, WarningIcon, ErrorIcon } from './styles';
import { getLiveWeekStartAndEnd } from '../../helpers';

const PARTIAL_WEEK_OUTSIDE_CURRENT_WEEK_MESSAGE = 'You are optimising outside of the current week and have specified an alternative start date and time for optimisation. Days in week to prior to this date and time will not be optimised.';
const CURRENT_WEEK_NO_PARTIAL_DATE_MESSAGE = 'You are optimising the current week without setting a start date and time. This will optimise the full week.';

const formatDateWithSlashes = date => moment(date).format('DD/MM/YYYY');
const formatDateTime = date => moment(date).format('DD/MM/YYYY HH:mm');
const getIfDatesEqual = (a, b) => moment(a).isSame(b, 'd');

const { liveWeekStartDate } = getLiveWeekStartAndEnd();

const TARGET_WEEK_PREFLIGHT_CHECK = ({ startOfWeekDate }) => {
  const isInActiveWeek = getIfDatesEqual(startOfWeekDate, liveWeekStartDate);
  return {
    title: `This optimisation will target the week starting ${formatDateWithSlashes(startOfWeekDate)}.`,
    description: !isInActiveWeek ? 'This is not the live week.' : null,
    displayPredicate: true,
    icon: isInActiveWeek ? InfoIcon : WarningIcon,
  };
};

const ALTERNATIVE_START_DATE_TIME_PREFLIGHT_CHECK = ({
  startOfWeekDate,
  endOfWeekDate,
  optimisationOptions: { startTime },
}) => {
  // If the date provided is outside the bounds of the period we wanna prevent optimisation.
  const isBeforeStart = startTime < moment(startOfWeekDate);
  const isAfterEnd = startTime > moment(endOfWeekDate).add(1, 'd');
  if (startTime && (isBeforeStart || isAfterEnd)) {
    return {
      title: 'Start time provided outside current period',
      description: `The provided start time ${formatDateTime(startTime)} is ${isBeforeStart ? 'before' : 'after'} the ${isBeforeStart ? 'beginning' : 'end'} of the current week. Please choose a date within the period to optimise.`,
      displayPredicate: true,
      icon: ErrorIcon,
      disableModal: true,
    };
  }

  const isInActiveWeek = getIfDatesEqual(startOfWeekDate, liveWeekStartDate);

  // It's considered non-standard behaviour if the user is trying to solve the current week without
  // providing a start from date.
  const isInActiveWeekWithoutPartialDate = isInActiveWeek && !startTime;

  // It's also considered non-standard behaviour if the user is not in
  // the active week and *has* set a start date
  const isInNonStandardPartialSolveState = (!isInActiveWeek && startTime)
    || isInActiveWeekWithoutPartialDate;

  const conditionalDescriptionMessages = [
    [isInActiveWeekWithoutPartialDate, CURRENT_WEEK_NO_PARTIAL_DATE_MESSAGE],
    [!isInActiveWeek, PARTIAL_WEEK_OUTSIDE_CURRENT_WEEK_MESSAGE],
  ];

  return {
    title: !startTime
      ? 'Optimising complete current week'
      : `This is a partial week optimisation, starting from ${formatDateTime(startTime)}.`,
    description: () => conditionalDescriptionMessages
      .map(([condition, message]) => condition && <p>{message}</p>),
    displayPredicate: !!startTime || isInActiveWeekWithoutPartialDate,
    icon: isInNonStandardPartialSolveState ? WarningIcon : InfoIcon,
  };
};

const PAST_OPTIMISATION_CONSIDERATION_PREFLIGHT_CHECK = ({
  optimisationOptions: { usePrevPlan },
}) => ({
  title: 'Past optimisation will not be considered',
  description: 'Past optimisations are not being considered. This may lead to inconsistent results.',
  displayPredicate: !usePrevPlan,
  icon: WarningIcon,
});

const NON_MAINTAINED_SHIFTS_CONSIDERATION_PREFLIGHT_CHECK = ({ nonMaintainedShiftCount }) => ({
  title: 'Non maintained shifts in period',
  description: `There ${nonMaintainedShiftCount !== 1 ? 'are' : 'is'} ${nonMaintainedShiftCount} shift${nonMaintainedShiftCount !== 1 ? 's' : ''} in this period that may be affected by optimisation.`,
  displayPredicate: nonMaintainedShiftCount > 0,
  icon: WarningIcon,
});

const SYNCS_IN_PROGRESS_PREFLIGHT_CHECK = ({ syncsInProgress }) => ({
  title: 'Data sync in progress',
  description: `A data sync is currently in progress so the data will not be up to date for this optimisation.`,
  displayPredicate: syncsInProgress,
  icon: ErrorIcon,
});

export const OPTIMISATION_PRE_FLIGHT_CHECKS = [
  TARGET_WEEK_PREFLIGHT_CHECK,
  ALTERNATIVE_START_DATE_TIME_PREFLIGHT_CHECK,
  PAST_OPTIMISATION_CONSIDERATION_PREFLIGHT_CHECK,
  NON_MAINTAINED_SHIFTS_CONSIDERATION_PREFLIGHT_CHECK,
  SYNCS_IN_PROGRESS_PREFLIGHT_CHECK,
];
